/**
 * AdminProjectPickerDialog.jsx
 *
 * Admin-only dialog that lists ALL projects owned by admin + editor-role
 * accounts so David (or any site admin) can browse and open any novel
 * directly in The Loom workspace.
 *
 * Triggered via:
 *   - File → "Open Admin Project…" menu item
 *   - window event: loom:open-admin-project-picker
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Search, BookOpen, Loader2, ShieldAlert, ExternalLink,
  PenLine, ChevronRight, Calendar, Hash,
} from 'lucide-react';
import { usePW } from '@/lib/store';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
}

export function AdminProjectPickerDialog({ open, onOpenChange }) {
  const { loadProject } = usePW();
  const [q, setQ] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const debounce = useRef(null);
  const abortRef = useRef(null);

  const fetchProjects = useCallback(async (search) => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/workspace-projects`, {
        params: search ? { q: search } : {},
        withCredentials: true,
        signal: ctrl.signal,
        timeout: 15000,
      });
      if (abortRef.current !== ctrl) return;
      setProjects(data.projects || []);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED') return;
      toast.error(err?.response?.data?.detail || 'Could not load projects.');
      setProjects([]);
    } finally {
      if (abortRef.current === ctrl) setLoading(false);
    }
  }, []);

  // Load projects when dialog opens or search changes
  useEffect(() => {
    if (!open) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchProjects(q), q ? 300 : 0);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [open, q, fetchProjects]);

  // Reset when closed
  useEffect(() => { if (!open) { setQ(''); setProjects([]); setLoadingId(null); } }, [open]);

  const openInWorkspace = async (project) => {
    setLoadingId(project.id);
    try {
      await loadProject(project.id);
      toast.success(`Opened "${project.title}"`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open project.');
    } finally {
      setLoadingId(null);
    }
  };

  const openInNewTab = (project) => {
    window.open(`/app?project=${encodeURIComponent(project.id)}`, '_blank');
  };

  // Group by owner
  const grouped = projects.reduce((acc, p) => {
    const key = p.owner_id;
    if (!acc[key]) acc[key] = { name: p.owner_name, email: p.owner_email, isAdmin: p.is_owner_admin, items: [] };
    acc[key].items.push(p);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 flex flex-col max-h-[88vh]"
        data-testid="admin-project-picker-dialog"
      >
        {/* Header */}
        <DialogHeader className="shrink-0 border-b border-border bg-panel-header/40 px-5 py-3">
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <ShieldAlert className="h-4 w-4 text-amber-500" />
            Open Admin Project
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            All novels from admin and editor accounts.
            Click a project to load it into the current workspace.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="shrink-0 border-b border-border px-4 py-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title or author…"
              className="pl-9 h-8 text-[12.5px]"
              data-testid="admin-picker-search"
              autoFocus
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-4">
          {loading && (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!loading && projects.length === 0 && (
            <div className="flex h-32 items-center justify-center text-[12.5px] text-muted-foreground" data-testid="admin-picker-empty">
              {q ? 'No projects match that search.' : 'No admin or editor projects found.'}
            </div>
          )}
          {!loading && Object.values(grouped).map((group) => (
            <div key={group.email} className="space-y-1.5">
              {/* Owner heading */}
              <div className="flex items-center gap-2 pb-0.5">
                <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider ${group.isAdmin ? 'bg-amber-100 text-amber-800 border border-amber-300/70' : 'bg-secondary text-muted-foreground border border-border'}`}>
                  {group.isAdmin ? <ShieldAlert className="h-2.5 w-2.5" /> : <PenLine className="h-2.5 w-2.5" />}
                  {group.isAdmin ? 'Admin' : 'Editor'}
                </span>
                <span className="text-[12px] font-semibold">{group.name}</span>
                <span className="text-[11px] text-muted-foreground">{group.email}</span>
                <span className="ml-auto text-[10.5px] text-muted-foreground">{group.items.length} project{group.items.length !== 1 ? 's' : ''}</span>
              </div>
              {/* Project cards */}
              <div className="space-y-1">
                {group.items.map((project) => (
                  <div
                    key={project.id}
                    className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2 hover:border-primary/40 hover:bg-card/80 transition-colors"
                    data-testid={`admin-picker-card-${project.id}`}
                  >
                    {/* Cover thumbnail */}
                    <div className="h-10 w-7 shrink-0 overflow-hidden rounded-sm bg-secondary">
                      {project.cover_url ? (
                        <img src={project.cover_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <BookOpen className="h-3 w-3 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-[12.5px] font-semibold leading-tight">{project.title}</p>
                      <div className="mt-0.5 flex items-center gap-2 text-[10.5px] text-muted-foreground">
                        {project.genre && <span className="rounded-sm bg-secondary px-1 py-0.5">{project.genre}</span>}
                        <span className="flex items-center gap-0.5"><Hash className="h-2.5 w-2.5" />{project.chapters_count} ch.</span>
                        <span className="flex items-center gap-0.5"><Calendar className="h-2.5 w-2.5" />{fmt(project.updated_at)}</span>
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="shrink-0 flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11.5px]"
                        title="Open in new tab"
                        onClick={() => openInNewTab(project)}
                        data-testid={`admin-picker-newtab-${project.id}`}
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 px-2.5 text-[11.5px] gap-1"
                        onClick={() => openInWorkspace(project)}
                        disabled={loadingId === project.id}
                        data-testid={`admin-picker-open-${project.id}`}
                      >
                        {loadingId === project.id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <ChevronRight className="h-3 w-3" />}
                        Open
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer count */}
        {!loading && projects.length > 0 && (
          <div className="shrink-0 border-t border-border px-5 py-2 text-[11px] text-muted-foreground">
            {projects.length} project{projects.length !== 1 ? 's' : ''} across {Object.keys(grouped).length} account{Object.keys(grouped).length !== 1 ? 's' : ''}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AdminProjectPickerDialog;
